import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../AuthContext";
import { useI18n } from "../i18n";
import { Header, eur } from "../components/Frumy";
import { CheckCircle, Trash, ArrowRight, ArrowsClockwise, ShoppingBag, ArrowLeft } from "@phosphor-icons/react";

const CATS = ["streaming", "palestra", "software", "trasporti", "food delivery", "altro"];

function EditRow({ item, onChange, onRemove, testid }) {
  const { t, catLabel } = useI18n();
  return (
    <div data-testid={testid} className="frumy-card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <input value={item.custom_name} onChange={(e) => onChange({ ...item, custom_name: e.target.value })}
        data-testid={`${testid}-name`}
        className="flex-1 rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[var(--frumy-green)]" />
      <select value={item.category} onChange={(e) => onChange({ ...item, category: e.target.value })}
        data-testid={`${testid}-category`}
        className="rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm font-semibold bg-[var(--surface)] text-[var(--heading)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]">
        {CATS.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
      </select>
      <div className="flex items-center gap-3">
        <span className="text-sm font-bold text-[var(--heading)] whitespace-nowrap">
          {item.frequency ? eur(item.amount) : eur(item.total_all != null ? item.total_all : item.total_current_month)}
        </span>
        <button data-testid={`${testid}-remove`} onClick={onRemove}
          className="pill-btn grid place-items-center h-8 w-8 text-red-500 hover:bg-red-50" title={t("remove")}>
          <Trash size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
}

export default function Confirm() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("frumy_analysis");
    if (!raw) { nav("/upload"); return; }
    setData(JSON.parse(raw));
  }, [nav]);

  if (!data) return null;

  const setSub = (i, v) => setData({ ...data, subscriptions: data.subscriptions.map((s, idx) => idx === i ? v : s) });
  const rmSub = (i) => setData({ ...data, subscriptions: data.subscriptions.filter((_, idx) => idx !== i) });
  const setHab = (i, v) => setData({ ...data, habituals: data.habituals.map((s, idx) => idx === i ? v : s) });
  const rmHab = (i) => setData({ ...data, habituals: data.habituals.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const account_name = sessionStorage.getItem("frumy_account") || "";
      await axios.post(`${API}/scans`, {
        subscriptions: data.subscriptions, habituals: data.habituals,
        period_end: data.period_end, account_name,
      });
      sessionStorage.removeItem("frumy_analysis");
      sessionStorage.removeItem("frumy_account");
      toast.success("Scansione salvata");
      nav("/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <button data-testid="confirm-back-btn" onClick={() => nav("/upload")}
            className="pill-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)] mb-4">
            <ArrowLeft size={16} aria-hidden />{t("back")}
          </button>
          <div className="flex items-center gap-2 text-[var(--frumy-green)]">
            <CheckCircle weight="fill" size={22} aria-hidden />
            <span className="text-xs tracking-[0.2em] uppercase font-bold">
              {data.subscriptions.length + data.habituals.length} voci rilevate
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">{t("reviewTitle")}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-2">{t("reviewSub")}</p>

          <section className="mt-8">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold mb-3">
              <ArrowsClockwise size={20} className="text-[var(--frumy-green)]" aria-hidden />{t("subscriptions")}
            </h2>
            <div className="space-y-3">
              {data.subscriptions.map((s, i) => (
                <EditRow key={i} item={s} testid={`sub-row-${i}`} onChange={(v) => setSub(i, v)} onRemove={() => rmSub(i)} />
              ))}
              {data.subscriptions.length === 0 && <p className="text-sm text-[var(--text-muted)]">—</p>}
            </div>
          </section>

          <section className="mt-8">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold mb-3">
              <ShoppingBag size={20} className="text-[var(--frumy-lock)]" aria-hidden />{t("habituals")}
            </h2>
            <div className="space-y-3">
              {data.habituals.map((h, i) => (
                <EditRow key={i} item={h} testid={`hab-row-${i}`} onChange={(v) => setHab(i, v)} onRemove={() => rmHab(i)} />
              ))}
              {data.habituals.length === 0 && <p className="text-sm text-[var(--text-muted)]">—</p>}
            </div>
          </section>

          <div className="sticky bottom-4 mt-8">
            <button data-testid="confirm-save-btn" onClick={save} disabled={saving}
              className="pill-btn w-full flex items-center justify-center gap-2 bg-[var(--frumy-navy)] hover:bg-black text-white font-bold py-3.5 disabled:opacity-60 shadow-lg">
              {saving ? "..." : t("confirm")}{!saving && <ArrowRight weight="bold" size={18} aria-hidden />}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
